// ============================================================
// TARGET PATH IN YOUR REPO:
//   app/src/app/components/skyplus/wf-runtime/services/wf-document-type-api.service.ts
// ============================================================
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { AuthenticationService, AppConfigService } from '@alfresco/adf-core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';

/**
 * Strike C — frontend client for the document-type registry.
 *
 * <p>Mirrors {@code WfReferenceDataApiService} exactly: per-request Bearer
 * headers (no HTTP interceptor), base URL from {@code skyplus.apiUrl}, and a
 * small per-instance cache in front of {@code GET /{typeKey}} so the capture
 * screen / document library / advanced search dedupe repeated fetches. Any
 * mutating call clears the cache; external callers can use {@link #invalidate}.
 *
 * <p>Used by:
 * <ul>
 *   <li>the wf-admin Document Types studio for full CRUD;</li>
 *   <li>(Strike D) the capture screen + document library + advanced search,
 *       which read {@link #listAll}/{@link #get} to render types, fields, and
 *       folder rules at runtime instead of the static document-types.config.ts.</li>
 * </ul>
 */
@Injectable({ providedIn: 'root' })
export class WfDocumentTypeApiService {

  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthenticationService);
  private readonly appConfig = inject(AppConfigService);

  private readonly base: string;

  /** Cache of {@code GET /{typeKey}} observables to dedupe inflight + repeated calls. */
  private readonly cache = new Map<string, Observable<DocumentType>>();

  constructor() {
    const apiUrl = this.appConfig.get<string>('skyplus.apiUrl', '/bpm/api');
    this.base = `${apiUrl}/wf/document-types`;
  }

  /** List all document types (summary projection). */
  listAll(activeOnly = false): Observable<DocumentTypeSummary[]> {
    const params = new HttpParams().set('activeOnly', String(activeOnly));
    return this.http.get<DocumentTypeSummary[]>(this.base, {
      headers: authHeaders(this.auth),
      params
    });
  }

  /** Full type with all its fields. Cached per {@code typeKey} for the service lifetime. */
  get(typeKey: string): Observable<DocumentType> {
    const cached = this.cache.get(typeKey);
    if (cached) { return cached; }
    const live = this.http.get<DocumentType>(`${this.base}/${encodeURIComponent(typeKey)}`, {
      headers: authHeaders(this.auth)
    }).pipe(
      shareReplay({ bufferSize: 1, refCount: false }),
      catchError(err => {
        this.cache.delete(typeKey);
        return throwError(() => err);
      })
    );
    this.cache.set(typeKey, live);
    return live;
  }

  /** Soft variant of {@link #get} — resolves to {@code null} on 404 / any error. */
  getOrNull(typeKey: string): Observable<DocumentType | null> {
    return this.get(typeKey).pipe(catchError(() => of(null)));
  }

  create(req: CreateDocumentTypeRequest): Observable<DocumentType> {
    return this.http.post<DocumentType>(this.base, req, {
      headers: authHeadersJson(this.auth)
    }).pipe(tap(() => this.cache.clear()));
  }

  update(typeKey: string, req: UpdateDocumentTypeRequest): Observable<DocumentType> {
    return this.http.put<DocumentType>(`${this.base}/${encodeURIComponent(typeKey)}`, req, {
      headers: authHeadersJson(this.auth)
    }).pipe(tap(() => this.invalidate(typeKey)));
  }

  /**
   * Round 7 — resolve folder segments + folio for one document from the
   * type's folder rules. The SINGLE engine behind Upload & Index, the
   * workflow File Structure Index, index-later, and the hot folder.
   * allocate=false for live preview (peeks the folio); true exactly once
   * when committing a document.
   */
  resolveFolders(typeKey: string, values: Record<string, unknown>,
                 opts?: { wfInstanceId?: number; allocate?: boolean }): Observable<ResolveFoldersResponse> {
    return this.http.post<ResolveFoldersResponse>(
      `${this.base}/${encodeURIComponent(typeKey)}/resolve-folders`,
      { values, wfInstanceId: opts?.wfInstanceId ?? null, allocate: opts?.allocate === true },
      { headers: authHeadersJson(this.auth) });
  }

  /** Active workflows for the "Associated workflow" picker. */
  listWorkflows(): Observable<{ wfKey: string; displayName: string }[]> {
    const apiUrl = this.appConfig.get<string>('skyplus.apiUrl', '/bpm/api');
    return this.http.get<any[]>(`${apiUrl}/wf/definitions`, { headers: authHeaders(this.auth) })
      .pipe(map(list => (list || []).map(d => ({ wfKey: d.wfKey, displayName: d.displayName || d.wfKey }))));
  }

  /** Retention schedules (RM) for the "Retention schedule" association dropdown. */
  listRetentionSchedules(): Observable<RetentionScheduleOption[]> {
    const apiUrl = this.appConfig.get<string>('skyplus.apiUrl', '/bpm/api');
    return this.http.get<any[]>(`${apiUrl}/rm/schedules`, { headers: authHeaders(this.auth) })
      .pipe(
        map(list => (list || []).map(s => ({
          code: s.code, name: s.name, department: s.department,
          retainYears: s.retainYears, finalAction: s.finalAction
        }))),
        catchError(() => of([]))
      );
  }

  /** Form-field keys a workflow's forms expose — for the per-field "Form field" picker. */
  workflowFormFields(wfKey: string): Observable<string[]> {
    const apiUrl = this.appConfig.get<string>('skyplus.apiUrl', '/bpm/api');
    return this.http.get<string[]>(
      `${apiUrl}/wf/definitions/${encodeURIComponent(wfKey)}/form-fields`,
      { headers: authHeaders(this.auth) });
  }

  delete(typeKey: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/${encodeURIComponent(typeKey)}`, {
      headers: authHeaders(this.auth)
    }).pipe(tap(() => this.invalidate(typeKey)));
  }

  /** Drop a cached type — call after a known-external update. */
  invalidate(typeKey?: string): void {
    if (typeKey) { this.cache.delete(typeKey); }
    else         { this.cache.clear(); }
  }
}

function authHeaders(auth: AuthenticationService): HttpHeaders {
  const token = auth.getToken();
  return token
    ? new HttpHeaders({ 'Authorization': `Bearer ${token}` })
    : new HttpHeaders();
}

function authHeadersJson(auth: AuthenticationService): HttpHeaders {
  return authHeaders(auth).set('Content-Type', 'application/json');
}

// ────────────────────────────────────────────────────────────────────────
// Types — mirror the backend DTOs (WfDocumentTypeDtos)
// ────────────────────────────────────────────────────────────────────────

export type DocumentFieldType =
  'text' | 'date' | 'number' | 'select' | 'textarea' | 'boolean'
  /** Keycloak search fields: users, roles, or either — rendered with the directory picker. */
  | 'person' | 'role' | 'personOrRole'
  /** Top-down "To" where each recipient carries its own optional "Through" (CEO memo shape). */
  | 'toThrough';

/** One field within a document type. Opaque to the backend; shaped here. */
export interface DocumentTypeField {
  /** Alfresco property name, e.g. 'eacc:hrEmploymentNumber'. */
  propertyName: string;
  label: string;
  type: DocumentFieldType;
  /** Show on the capture (indexing) form. */
  capture?: boolean;
  /** Required on capture. */
  mandatory?: boolean;
  /** Exposed as a search field in advanced/contextual search. */
  search?: boolean;
  /** Shown as a column in the document library list header (like {@link search}, but for the table). */
  column?: boolean;
  /** F3 — hidden from the capture/memo wizard AND the filing form, but still offered as a
   *  dashboard / step-list column (e.g. a folio file reference surfaced via {@link systemSource}). */
  hidden?: boolean;
  /** When set + enabled, this field's value builds an ordered sub-folder. */
  folderSegment?: { enabled?: boolean; order?: number; useLabel?: boolean };
  /** Inline options for a select field. */
  options?: { value: string; label: string }[];
  /** Pull select options from a reference-data list key instead of inline options. */
  refList?: string;
  maxLength?: number;
  pattern?: string;
  placeholder?: string;
  defaultValue?: string;
  /** Auto-fill with the current username on capture. */
  autoPopulateUserId?: boolean;
  /** Auto-fill with the current date on capture. */
  autoPopulateDate?: boolean;
  /**
   * Hot-folder capture: the sidecar XML field name (<field name="..."/>) that
   * maps to this property. Defaults to the label when blank. Persists in the
   * fields JSON; no backend change required.
   */
  xmlFieldName?: string;

  /**
   * Hot-folder filename capture: the token that populates this field when a capture's
   * extraction mode is "filename" — the 1-based token position (delimiter mode) or the
   * regex group index/name (regex mode). Mirrors {@link xmlFieldName}. Persists in the
   * fields JSON; no backend change required.
   */
  filenameToken?: string;
  /**
   * Memo/letter template fill: the Word-template token this field's value
   * replaces, matched as ${token} / {{token}} in the document body, headers,
   * footers and tables. Blank = the field is not injected into the document
   * (it may still be stamped as an aspect property). Persists in the fields
   * JSON; no backend change required.
   */
  docxPlaceholder?: string;
  /** Phase 4 — auto-fill this field's Word token with the system reference/folio on filing
   *  (leaves ${token} intact at creation). Values: 'fileReference' | 'folioNumber'. */
  systemSource?: string;
  /** Feature 2/7 — wizard shows a repeatable +Add list; the Word token repeats per value. */
  multiValue?: boolean;
  docxRepeat?: boolean;
  /**
   * For a person/role field: its role in the approval chain. When set, the wizard's picked
   * user(s)/role(s) flow into `approverQueue` in order Through → To (the Through receives first and
   * forwards to the To). Blank = the field is display-only (e.g. From = originator, not an approver).
   */
  chainRole?: 'from' | 'through' | 'to' | 'copyTo';
  /**
   * What this slot prints in the document. When the picked value is a role resolved to a person:
   *  - unset (default) → the picked person's DESIGNATION for chain-role fields (To/Through/From).
   *  - true  → force the designation.
   *  - false → print the ROLE / display name instead (org that doesn't use designations).
   * Routing is unaffected either way (it uses the role/user id).
   */
  printDesignation?: boolean;
  /** Chain action for this field's entries: review (forward) or sign. Default: through=review, to=sign. */
  chainAction?: 'review' | 'sign';
  /**
   * For a From field (chainRole='from') — when to place the From in the approval chain:
   *  - 'ifNotSelf' (default) → only when the drafter is NOT the From (officer drafting on the owner's
   *     behalf → owner reviews first); skipped when the From drafts it themselves.
   *  - 'always' → always first in the chain, even when self-drafted.
   *  - 'never' → the From is display-only, never an approver.
   */
  chainWhen?: 'ifNotSelf' | 'always' | 'never';
  /**
   * Auto-fill this (text) field from a directory field's selection made elsewhere on the form.
   * The value names the SOURCE:
   *  - a source field's `propertyName` → copy that field's resolved person's name here, or
   *  - the shortcut `'fromSignatory'` → the field whose `chainRole='from'` (whatever its name).
   * "Resolved person" = a picked user, or the chosen member of a picked role (a one-member role is
   * automatic; a multi-member role prompts who signs). The field stays editable; a manual edit is
   * preserved (only blank / previously auto-filled values are overwritten). Blank = no auto-fill.
   */
  autoFillFrom?: string;
  /**
   * Which attribute of the auto-fill source person to capture into this field.
   * Only used when {@link autoFillFrom} is set. Blank/'name' = the person's display name
   * (the original footer-name behaviour). The org values come from wf_user_directory (HR sync):
   *  - 'designation' → the person's title
   *  - 'department' / 'division' / 'directorate' → that org level's name
   *  - 'autoBySection' → division name when the person's section_type is 'division', else department name
   * Populates the aspect field only (like footer name); no docx change.
   */
  captureAttr?: 'name' | 'designation' | 'department' | 'division' | 'directorate' | 'autoBySection';
  /**
   * Which capture surface this field appears on:
   *  - 'memo'   → only the memo/letter creation wizard
   *  - 'filing' → only the filing screen (a later, extra detail)
   *  - 'both'   → both (default when blank)
   * Lets a single document type drive the create wizard and the filing form
   * from one field list. Persists in the fields JSON; no backend change.
   */
  captureStage?: 'memo' | 'filing' | 'both';
  /**
   * Capture-once bridge: the workflow form field key (Form.io key, e.g.
   * 'subject') this field's value is also written to at instance start, so the
   * wizard value populates the workflow form / dashboard / search — not just
   * the aspect. Blank = not bridged to the form. Persists in the fields JSON.
   */
  formField?: string;
  /**
   * For a user-search field ({@code person} or {@code personOrRole}): restrict the people search to
   * members of these realm role(s), comma-separated (e.g. 'ICT, Finance'). Blank = search everyone.
   * The results are the union of all listed roles' members, de-duplicated. Role names must match
   * Keycloak exactly. Rendered onto the directory picker as its {@code restrictRoles} setting.
   */
  restrictRoles?: string;
}

/** A retention schedule option for the Document Type association dropdown. */
export interface RetentionScheduleOption {
  code: string;
  name: string;
  department?: string;
  retainYears?: number;
  finalAction?: string;
}

export interface DocumentTypeSummary {
  id: number;
  typeKey: string;
  label: string;
  description?: string;
  nodeType: string;
  icon?: string;
  color?: string;
  baseFolderPath?: string;
  /** Alfresco site this type files into (document-type-first filing site). */
  filingSiteId?: string;
  active: boolean;
  sortOrder: number;
  fieldCount: number;
  aspectNames?: string[];
}

/**
 * Round 7 — one level of a document type's folder rule chain.
 * source: 'field' | 'datePart' | 'literal' | 'composite'.
 */
export interface FolderRuleLevel {
  source: 'field' | 'datePart' | 'literal' | 'composite' | 'orgDimension';
  field?: string;
  useLabel?: boolean;
  /** For source='orgDimension': which org level of the From person to use as this folder segment.
   *  Resolved from Keycloak/HR; absent (person has no such level, or a non-Keycloak site) → onMissing (default skip). */
  orgLevel?: 'directorate' | 'department' | 'division' | 'commission';
  /** For source='orgDimension': use the level's short code instead of its name for the folder label. */
  useCode?: boolean;
  granularity?: 'FULL_DATE' | 'YEAR' | 'YEAR_MONTH' | 'YEAR_MONTH_DAY' | 'MONTH' | 'QUARTER' | 'FINANCIAL_YEAR';
  monthFormat?: 'numeric' | 'short' | 'long';
  text?: string;
  template?: string;
  onMissing?: 'required' | 'skip' | 'fallback' | 'later';
  fallback?: string;
}

export interface FolderRuleFolio {
  enabled?: boolean;
  scopeLevel?: number;
  pad?: number;
  reset?: 'never' | 'year' | 'fy';
  prefix?: string;
}

export interface FolderRules {
  levels?: FolderRuleLevel[];
  folio?: FolderRuleFolio;
}

/** Feature 3 — Filing Modes config on a document type. */
/** Top-down memo — read-only FYI distribution AFTER the workflow completes (the From signs). */
export interface FyiConfig {
  /** onSubmit | onCompletion | off  (absent/off = no distribution here). */
  trigger?: string;
  /** Chain-roles whose recipients get an FYI (e.g. ['to','owner']). */
  sourceRoles?: string[];
  /** Also FYI each To/Through pair's Through. */
  includeThrough?: boolean;
}

export interface FilingConfig {
  /** Which modes are offered; empty/absent = no prompt (today's behaviour). */
  modes?: string[];                 // e.g. ['rule','browse']
  rule?: { label?: string };        // RULE mode uses folderRules
  browse?: {
    label?: string;
    rootPath?: string;              // where browsing starts
    obeyFolderDocType?: boolean;    // index using the picked folder's mapped type
    indexingOptional?: boolean;     // allow proceeding without re-indexing
  };
  /** Org-hierarchy filing (Phase 4): file "General" under the From person's
   *  directorate/department/division (from Keycloak) instead of folder rules.
   *  Falls back to folder rules when the From person has no hierarchy. */
  orgDimension?: {
    enabled?: boolean;
    levels?: string[];              // default ['directorate','department','division'] (top → down)
    useLabel?: 'name' | 'code';     // folder label source, default 'name'
    dedupeAdjacent?: boolean;       // collapse repeated adjacent names, default true
  };
}

export interface ResolveFoldersResponse {
  segments: string[];
  folio?: string;
  folioNumber?: number;
  errors: Record<string, string>;
  preview: string;
  /** Tenant's folio stamping contract from System Settings (e.g.
   *  bandari:folioed at Bandari). Frontends use these instead of
   *  hardcoding a model prefix. */
  folioAspect?: string;
  folioNumberProperty?: string;
  folioFolderProperty?: string;
}

export interface DocumentType {
  id: number;
  typeKey: string;
  label: string;
  description?: string;
  nodeType: string;
  /** Associated workflow (optional). */
  wfKey?: string;
  captureWfMode?: string;
  captureWfOutcome?: string;
  captureWfMatchPattern?: string;
  aspectNames: string[];
  baseFolderPath?: string;
  /** Alfresco site this type files into (document-type-first filing site). */
  filingSiteId?: string;
  icon?: string;
  color?: string;
  fields: DocumentTypeField[];
  folderRules?: FolderRules;
  /** Feature 3 — filing modes config (rule / browse). */
  filingConfig?: FilingConfig;
  /** Top-down memo — read-only FYI distribution after signing. */
  fyiConfig?: FyiConfig;
  searchNameField?: boolean;
  searchFileReference?: boolean;
  /** RM — retention schedule code governing this type (null/'' = none). */
  retentionScheduleCode?: string;
  sortOrder?: number;
  active: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateDocumentTypeRequest {
  typeKey: string;
  label: string;
  description?: string;
  nodeType: string;
  wfKey?: string;
  captureWfMode?: string;
  captureWfOutcome?: string;
  captureWfMatchPattern?: string;
  aspectNames?: string[];
  baseFolderPath?: string;
  /** Alfresco site this type files into (document-type-first filing site). */
  filingSiteId?: string;
  icon?: string;
  color?: string;
  fields?: DocumentTypeField[];
  folderRules?: FolderRules;
  /** Feature 3 — filing modes config (rule / browse). */
  filingConfig?: FilingConfig;
  /** Top-down memo — read-only FYI distribution after signing. */
  fyiConfig?: FyiConfig;
  searchNameField?: boolean;
  searchFileReference?: boolean;
  retentionScheduleCode?: string;
  sortOrder?: number;
}

export interface UpdateDocumentTypeRequest {
  label: string;
  description?: string;
  nodeType: string;
  wfKey?: string;
  captureWfMode?: string;
  captureWfOutcome?: string;
  captureWfMatchPattern?: string;
  aspectNames?: string[];
  baseFolderPath?: string;
  /** Alfresco site this type files into (document-type-first filing site). */
  filingSiteId?: string;
  icon?: string;
  color?: string;
  fields?: DocumentTypeField[];
  folderRules?: FolderRules;
  /** Feature 3 — filing modes config (rule / browse). */
  filingConfig?: FilingConfig;
  /** Top-down memo — read-only FYI distribution after signing. */
  fyiConfig?: FyiConfig;
  searchNameField?: boolean;
  searchFileReference?: boolean;
  retentionScheduleCode?: string;
  sortOrder?: number;
  active: boolean;
}